import { useEffect, useState } from "react";
import { useFloodgateFeature } from "./useFloodgateFeature";

// VP9 levels refer to a minimum "pixels per second" that the decoder must be able to handle.
// Refer to https://www.webmproject.org/vp9/levels/
function getVP9Level(codedHeight: number): number {
  if (codedHeight <= 240) {
    return 20;
  } else if (codedHeight <= 360) {
    return 21;
  } else if (codedHeight <= 480) {
    return 30;
  } else if (codedHeight <= 720) {
    return 31;
  } else if (codedHeight <= 1080) {
    return 40;
  }

  return 50;
}

const boolString = (value: boolean): string => (value ? "true" : "false");

async function checkVP9DecoderSupport(pixelHeight: number): Promise<boolean> {
  const level = getVP9Level(pixelHeight);
  const config: VideoDecoderConfig = {
    codec: `vp09.00.${level}.08`,
    hardwareAcceleration: "prefer-hardware",
    codedWidth: (16 * pixelHeight) / 9,
    codedHeight: 360,
  };

  const result = await VideoDecoder.isConfigSupported(config);
  if (result) {
    return result.supported || false;
  }

  return false;
}

export function useWebGPUSurvey(): void {
  const [surveyComplete, setSurveyComplete] = useState(false);
  const surveyEnabled = useFloodgateFeature("webgpu_survey_enabled");

  useEffect(() => {
    async function performWebGPUSurvey(): Promise<void> {
      const webgpuSupported = boolString(!!navigator.gpu);

      let webcodecsSupported = "false";
      let vp9_360p_supported = "false";
      let vp9_720p_supported = "false";
      let vp9_1080p_supported = "false";
      let vp9_2160p_supported = "false";
      const deviceMemory = window.navigator.deviceMemory;
      const hardwareConcurrency = window.navigator.hardwareConcurrency;

      if (!!VideoDecoder?.isConfigSupported) {
        webcodecsSupported = "true";
        vp9_360p_supported = boolString(await checkVP9DecoderSupport(360));
        vp9_720p_supported = boolString(await checkVP9DecoderSupport(720));
        vp9_1080p_supported = boolString(await checkVP9DecoderSupport(1080));
        vp9_2160p_supported = boolString(await checkVP9DecoderSupport(2160));
      }

      const survey = {
        deviceMemory,
        hardwareConcurrency,
        webgpuSupported,
        webcodecsSupported,
        vp9_360p_supported,
        vp9_720p_supported,
        vp9_1080p_supported,
        vp9_2160p_supported,
      };

      if (process.env.NODE_ENV === "development") {
        console.log("WEBGPU_SURVEY", survey);
      }
      newrelic.addPageAction("WEBGPU_SURVEY", survey);

      setSurveyComplete(true);
    }

    if (surveyEnabled && !surveyComplete) {
      performWebGPUSurvey();
    }
  }, [surveyComplete, surveyEnabled]);
}
